<template>
  <!-- Page Content -->
  <Transition name="fade" mode="out-in">
    <div
        class="w-full scroll-smooth flex flex-col relative transition-colors duration-500 ease-in-out items-center relative"
        :style="pageStyles"
    >
      <!-- Fixed Navbar -->
      <NavBar ref="navBarRef"
              :company-name="pageData?.companyName"
              :nav-setup="pageData?.navSetup"
              :logo="pageData?.companyLogo"
              :available-pages="pageData?.availablePages"
              :class="navClass"
              :show-company-name="pageData?.showCompanyName"
              :start-dimmed="shown.length > 0 && shown[0].section.type !== 'hero'"
              :product-page-name="pageData?.productPageName"
              :showcase-page-name="pageData?.showcasePageName"
      />
      <PreviewEditor v-if="previewMode === true" />
      <LongSplitList       v-if="false"          :colors="pageData.brandColors"
      ></LongSplitList>
      <div v-if="pageData && pageData.pageType === 'explore-categories'" class="bg-transparent w-full transition-all duration-700 delay-200 flex items-center justify-center flex-col">
        <CategoryPage :category="pageData.category" :colors="colorStore.colors" :key="pageData.category"/>
      </div>
      <div v-else-if="!pageData || shown.length === 0" class="w-full transitional-all duration-1000 ease-in ease-out">
        <div
            class="h-screen w-full flex items-center justify-center flex-col"
            :style="{marginTop: `${navHeight}px`}"
        >
          <!-- In the studio an empty page (its last section removed, or all hidden) is a page to add to, not a missing one:
               the overlay puts "+ Add a section here" on this. -->
          <p v-if="(loaded || pageData) && studioMode !== 'off'" data-studio-empty class="px-6 text-center text-base opacity-70">This page has no sections yet.</p>
          <!-- Drawn on the server too (pageData is there), so the 404 response itself carries the friendly page. -->
          <PageError v-else-if="loaded || pageData"></PageError>
        </div>
      </div>
      <div v-else class="w-full transition-all duration-700 ease-in ease-out">
        <div class="w-full transition-all duration-700 ease-in ease-out" v-for="({section, index: fullIndex, key}, index) in shown" :key="key">
          <div
              class="bg-transparent w-full transition-all duration-700 delay-200 flex items-center justify-center flex-col "
              :class="{relative: section.type === 'hero'}"
              :id="section.id"
              :data-studio-index="fullIndex"
              :data-studio-id="section.id"
              :data-studio-type="section.type"
              :data-words="section.type === 'hero' && ['light', 'dark'].includes(section.wordsColour) ? section.wordsColour : null"
              :style="{
              marginTop: section.type !== 'hero' && index === 0 ? `${navHeight}px` : '0',
              paddingTop: section.type === 'hero' && index === 0 ? 'var(--t-announce-h, 0px)' : null,
        }"
          >
            <!-- Hands every section the level of its main heading (H1 for one, H2 for the rest); draws no element. -->
            <SectionHeadingLevel :level="headingLevels[index]">

            <component
                :is="heroComponent"
                v-if="section.type === 'hero'"
                ref="heroRefs"
                :title="section.title"
                ptitle="pinnedHeroIndex"
                :content="section.content"
                :backgroundImage="section.image"
                :backgroundVideo="section.video"
                :fullHeight="section.fullHeight"
                :scrollTarget="heroScrollTarget(section, shown[index + 1]?.section.id)"
                :navigateTo="section.navigateTo"
                :callToAction="section.callToAction"
                :display-only="section.displayOnly ?? false"
                :black-text="section.blackText ?? false"
                :paragraph="section.paragraph ?? ''"
                :isFixed="pinnedHeroIndex === index"
                :layout="section.layout || lookHeroLayout(colorStore.theme, pageData?.look)"
                :images="Array.isArray(section.images) ? section.images : []"
                :heading-level="headingLevels[index]"
            />
            <!-- Website Studio only: the hero's empty photo, marked over its
                 background (heroes are themed, so it's drawn here once). -->
            <StudioPhotoSlot v-if="section.type === 'hero' && photoSlotEmpty(section)" overlay />
            <!-- Section group B (src/sections, S2): new types, and Words /
                 photo-and-words / what-we-offer / photos once the studio saved
                 blocks or a layout. Anything else falls through as before. -->
            <component v-else-if="s2Component(section) && isSectionVisible(section)" :is="s2Component(section)"
                       v-bind="s2Props(section, {index, pageData})" />
            <!-- The shop's sections on website pages (SH2): products resolved in pages.js. -->
            <component v-else-if="shopSectionComponents[section.type] && isSectionVisible(section)"
                       :is="shopSectionComponents[section.type]" v-bind="section" />
            <template v-else>
              <!-- Web v2 sections (src/sections): new types, and today's types given a v2 layout. -->
              <component v-if="v2Component(section)" :is="v2Component(section)" :section="section" :index="index"
                         :data="pageData?.sectionData || {}" />
              <SectionContainer
                  v-else-if="WRAPPED_TYPES.has(section.type) && isSectionVisible(section)"
                  :title="section.title" class="w-full" :colors="pageData.brandColors"
                  :background-variant="index % 2 === 0 ? 1 : 2"
              >
                <component :is="sectionComponents[section.type]" v-bind="sectionProps(section, index)" />
              </SectionContainer>
              <component
                  v-else-if="sectionComponents[section.type] && isSectionVisible(section)"
                  :is="sectionComponents[section.type]"
                  v-bind="sectionProps(section, index)"
              />
            </template>
            <!-- Website Studio only (never SSR or live): a section with nothing
                 to show yet still needs something to outline and tap. -->
            <div v-if="studioMode !== 'off' && emptyPlaceholder(section)" class="studio-empty">
              {{ emptyPlaceholder(section) }}
            </div>
            </SectionHeadingLevel>
          </div>
        </div>
      </div>
      <!-- Floating Call to Action -->
      <FloatingCTA
          :ctaText="pageData?.ctaText"
          :ctaLink="pageData?.ctaLink"
          :colors="pageData?.brandColors"
      />


      <!-- Other Components -->
      <FooterSection
          @show:credits="showCredits"
          :class="shown.length % 2 === 0 ? ' ' : ''"
          :companyName="pageData?.realCompanyName"
          :companyTagline="pageData?.companyTagline"
          :registrationInfo="pageData?.pageFooter"
          :colors="pageData?.brandColors"
          :footer-links="footerLinks"
          :footerLinks1="[
        { label: 'Home', url: '/' },
        { label: pageData?.productPageName, url: productLink },
        { label: 'About', url: '/about' },
        { label: 'Privacy', url: '/privacy-policy' },
        { label: 'Terms', url: '/terms-of-service' }
          ]"
          :socialLinks="pageData?.socialLinks ?? []"
      :show-credits="pageData?.pexels?.length > 0"
      />
    </div>
</Transition>
  <!-- Subtle blur overlay and top loading bar when fetching -->
  <div
    v-if="isFetching"
    class="fixed inset-0 z-40 bg-white/30 backdrop-blur-sm transition-opacity duration-300 pointer-events-none"
  ></div>
  <div
    v-if="isFetching"
    class="fixed top-0 left-0 z-50 h-1.5 w-full overflow-hidden bg-transparent"
  >
    <div class="h-full animate-knight-rider" :style="backgroundColourAccent"></div>
  </div>
  <CreditsDialog :pexels="pageData?.pexels" ref="credits"></CreditsDialog>
</template>

<script setup>
import {computed, nextTick, onMounted, onUnmounted, ref, watch} from "vue";
import {usePageStore} from "@/stores/pageStore";
import {useColorStore} from "@/classes/useColorStore.js";
import {lookHeroLayout} from "@/themes/look.js";
import {useRoute} from "vue-router";
import {useHead} from "@vueuse/head";
import NavBar from "@/components/NavBar.vue";
import SplitSection from "@/components/SplitSection.vue";
import TestimonialSection from "@/components/TestimonialSection.vue";
import GridSection from "@/components/GridSection.vue";
import FooterSection from "@/components/FooterSection.vue";
import {classicPolicyLinks} from "@/shop/policyLinks.js";
import FloatingCTA from "@/components/FloatingCTA.vue";
import CTASection from "@/components/CTASection.vue";
import {pageHeadingLevels} from "@/utils/headingLevels.js";
import SectionHeadingLevel from "@/components/SectionHeadingLevel.vue";
import ContactSection from "@/components/ContactSection.vue";
import ContactDetailsSection from "@/components/ContactDetailsSection.vue";
import CarouselPanel from "@/components/CarouselPanel.vue";
import ContentSection from "@/components/ContentSection.vue";
import FeaturesSection from "@/components/FeaturesSection.vue";
import ShowcaseSection from "@/components/ShowcaseSection.vue";
import DatasheetSection from "@/components/DatasheetSection.vue";
import PostHeaderSection from "@/components/PostHeaderSection.vue";
import PrettyDocumentHeader from "@/components/PrettyDocumentHeader.vue";
import SpacerSection from "@/components/SpacerSection.vue";
import {useBusinessStore} from "@/stores/businessStore.js";
import SectionContainer from "@/components/SectionContainer.vue";
import DownloadsSection from "@/components/DownloadsSection.vue";
import FilmStripSection from "@/components/FilmStripSection.vue";
import router from "@/router/index.js";
import CategoryPage from "@/components/CategoryPage.vue";
import ImageGrid from "@/components/ImageGrid.vue";
import PageError from "@/components/PageError.vue";
import LinksSection from "@/components/LinksSection.vue";
import CreditsDialog from "@/components/CreditsDialog.vue";
import SocialsList from "@/components/SocialsList.vue";
import PreviewEditor from "@/components/PreviewEditor.vue";
import FaqSection from "@/components/FaqSection.vue";
import AreasSection from "@/components/AreasSection.vue";
import {emptyPlaceholder, photoSlotEmpty, shownSections} from "@/utils/studioLogic.js";
import {ogData, ogMeta} from "@/utils/ogTags.js";
import StudioPhotoSlot from "@/components/StudioPhotoSlot.vue";
import {heroScrollTarget} from "@/utils/buttonTarget.js";
import {carouselHeight} from "@/utils/carouselHeight.js";
import {galleryCaptionsOn} from "@/utils/sectionMedia.js";
import {studioMode} from "@/classes/previewBridge.js";
import LongSplitList from "@/components/LongSplitList.vue";
import {useBrandCssVars} from "@/composables/useBrandCssVars.js";
import {useThemeTokens} from "@/composables/useThemeTokens.js";
import {useThemedComponent} from "@/composables/useThemedComponent.js";
import {s2Component, s2Props, v2Component} from "@/sections/index.js";
import {shopSectionComponents} from "@/shop/sections/index.js";

// Resolved per-theme — defaults to the shared full-bleed HeroSection.vue
// (registered as glass's "hero" slot) unless a theme overrides it entirely.
const heroComponent = useThemedComponent("hero");

// Theme-agnostic section components — hero is deliberately excluded, it keeps
// its own explicit branch above since it's resolved via the theme registry
// rather than a fixed static import.
const sectionComponents = {
  split: SplitSection,
  datasheet: DatasheetSection,
  spacer: SpacerSection,
  showcase: ShowcaseSection,
  testimonial: TestimonialSection,
  download: DownloadsSection,
  links: LinksSection,
  filmstrip: FilmStripSection,
  grid: GridSection,
  "image-grid": ImageGrid,
  content: ContentSection,
  features: FeaturesSection,
  cta: CTASection,
  contact: ContactSection,
  "contact-details": ContactDetailsSection,
  socialContact: SocialsList,
  "post-header": PostHeaderSection,
  "document-header": PrettyDocumentHeader,
  carousel: CarouselPanel,
  faq: FaqSection,
  areas: AreasSection,
};

const WRAPPED_TYPES = new Set(["download", "links", "carousel"]);

function isSectionVisible(section) {
  if (section.type === "links") return (section.links?.length ?? 0) > 0;
  return true;
}

function sectionProps(section, index) {
  const backgroundVariant = index % 2 === 0 ? 1 : 2;
  const colors = pageData.value.brandColors;
  switch (section.type) {
    case "split":
      return {
        title: section.title, content: section.content, callToAction: section.callToAction,
        link: section.link, src: section.image, textLeft: section.textLeft,
        colors, backgroundVariant,
      };
    case "datasheet":
      return {
        title: section.title, subtitle: section.subtitle, backgroundImage: section.image,
        backgroundVideo: section.video, fullHeight: section.fullHeight, scrollTarget: section.scrollTarget,
        navigateTo: section.navigateTo, callToAction: section.callToAction, colors,
      };
    case "spacer":
      return { id: section.id };
    case "showcase":
      return {
        title: section.title, content: section.content, detail: section.detail,
        callToAction: section.callToAction, link: section.link, video: section.video,
        src: section.image, textLeft: section.textLeft, colors, backgroundVariant,
        first: index === 1,
      };
    case "testimonial":
      return { title: section.title, content: section.content, subText: section.subText, backgroundVariant, colors };
    case "download":
      return { colors, title: section.title, attachments: section.attachments, backgroundVariant };
    case "links":
      return { title: section.title, subText: section.subText, colors, links: section.links, backgroundVariant };
    case "filmstrip":
      return { colors, images: section.images };
    case "grid":
      return {
        title: section.title, description: section.description, items: section.items,
        columns: section.columns, colors, backgroundVariant,
      };
    case "image-grid":
      return {
        title: index === 0 ? pageData.value.pageTitle : "", images: section.images,
        backgroundVariant, colors, sharable: false, showCaptions: galleryCaptionsOn(section),
      };
    case "content":
      return {
        title: section.title, colors, content: section.content, image: section.image,
        video: section.video, backgroundVariant, sharable: section.sharable,
        forceBlandColors: section.forceBlandColors, price: section.price,
        buttonText: section.buttonText, buttonLink: section.buttonLink,
      };
    case "features":
      return { features: section.features, colors, backgroundVariant, title: section.title, description: section.description };
    case "cta":
      return {
        title: section.title, content: section.content, scrollTarget: section.scrollTarget,
        navigateTo: section.navigateTo, callToAction: section.callToAction, colors, backgroundVariant,
      };
    case "contact":
      return { title: section.title, description: section.description || section.content, colors, backgroundVariant };
    case "contact-details":
      return {
        title: section.title, description: section.description || section.content, phone: section.phone,
        email: section.email, includedIds: section.includedIds, address: section.address,
        colors, backgroundVariant,
      };
    case "socialContact":
      return {
        colors, backgroundVariant,
        facebook: section.facebook.url, X: section.X.url, instagram: section.instagram.url,
        linkedIn: section.linkedIn.url, youtube: section.youtube.url, tiktok: section.tiktok.url,
        pinterest: section.pinterest.url,
      };
    case "post-header":
      return { colors, images: section.images, title: section.title };
    case "document-header":
      return { title: section.title, colors, subtitle: section.subtitle, backgroundImage: section.image };
    case "carousel":
      // An inline height: a stored Tailwind class was never compiled, so it drew nothing.
      return { style: {height: carouselHeight(section.height)}, slides: section.images || [] };
    case "faq":
      return { title: section.title, items: section.items, colors, backgroundVariant };
    case "areas":
      return {
        title: section.title, content: section.content, areas: section.areas,
        image: section.image && typeof section.image === "object" ? section.image : { url: "" }, colors, backgroundVariant,
      };
    default:
      return { colors, backgroundVariant };
  }
}

const heroRefs = ref([]);
const pinnedHeroIndex = ref(0);
const store = useBusinessStore();
// ✅ Get Pinia store
const pageStore = usePageStore();
const colorStore = useColorStore();
const pageData = computed(() => pageStore.pageData); // ✅ Access injected data
// What gets drawn: sections marked `hidden: true` (Website Studio) are left
// out everywhere, live and preview. Each entry keeps its index in the full
// list (the studio's insert points count hidden ones) and a unique key, so a
// live draft re-renders in place instead of remounting every section.
const shown = computed(() => shownSections(pageData.value?.sections));
// One H1 per page: the first hero with a title, else the first section that draws a heading (see utils/headingLevels.js).
const headingLevels = computed(() => pageHeadingLevels(shown.value.map((s) => s.section)));
const route = useRoute();
const credits = ref(null);
const previewMode = ref(false);
const loaded = ref(false);
const heroCounter = ref(0);
const herosReady = ref(false);

const fetchPageData = async (companyId, pageName) => {
  try {
    // pageStore.setPageData(null); // ✅ Clear existing data safely
    // const urlParts = router.route.split("/");
    // let pageType = urlParts[1] || "home";
    // let pageName = urlParts[2] || "";

    // if (!["home", "about", "contact", "products", "services", "what-we-do", "posts", "categories", "post", "posts", "showcase", "privacy-policy", "terms-of-service"].includes(pageType)) {
    //   pageName = urlParts[1];
    //   pageType = "page";
    // }
    loaded.value = false;
    let prefix = '';
    if (import.meta.env.VITE_IS_PREVIEW_SERVER === 'true'){
      prefix = '/api/preview'
      previewMode.value = true;
    }

    const response = await fetch(`${prefix}/api/page-data/${companyId}/${pageName}`);
    let data = await response.json();
    if (data && data.error === "attempt refresh") {
      await fetch(`/api/token/refresh_token`, {
        method: 'POST',
        credentials: 'include'
      });
      const retry = await fetch(`${prefix}/api/page-data/${companyId}/${pageName}`);
      data = await retry.json();
    }
    // A retired page's old address, reached in the browser: go where it now points.
    if (data && typeof data.redirect === "string" && data.redirect.startsWith("/") && !data.redirect.startsWith("//")) {
      await router.replace(data.redirect);
      return;
    }
    pageStore.setPageData(data); // ✅ Correctly updating the store
    colorStore.setColors(data.brandColors);
    loaded.value = true;
    // updateMetaTags();
  } catch (error) {
    console.error("❌ Fetch Error:", error);
    window.location.reload();
  }
};

const isFetching = ref(true)

watch(
    () => route.path,
    async () => {
      if (isFetching.value) return; // ✅ Prevent multiple calls
      isFetching.value = true;

      const segments = route.path.split("/");
      const pageType = route.path === '/' ? 'home' : segments[1];
      const pageName = segments.length > 2 ? segments[segments.length - 1] : '';
      await fetchPageData(pageType, pageName);

      isFetching.value = false;
      window.scrollTo(0, 0);
      await nextTick();
      heroRefs.value = [];
      await nextTick();
      heroRefs.value = (Array.isArray(heroRefs.value) ? heroRefs.value : []).filter(Boolean);
      pinnedHeroIndex.value = 0;
      await new Promise(resolve => setTimeout(resolve, 50));
      herosReady.value = true;
      updatePinnedHero();
    }
);

const navHeight = ref(0);
const navBarRef = ref(null);

import { onMounted as vueOnMounted } from "vue";

const footerLinks = computed(() => {
  // Build footer links in the same sample order used when passing to FooterSection
  const links = [];

  // Always include Home
  links.push({ label: 'Home', url: '/' });

  // Include product page link if the page config indicates a product page exists
  // availablePages.products is used elsewhere (NavBar) to indicate presence of a product page
  const hasProducts = !!(pageData.value && pageData.value.availablePages && pageData.value.availablePages.products);
  if (hasProducts && pageData.value.productPageName) {
    links.push({ label: pageData.value.productPageName, url: productLink.value });
  }

  // Include About page if available
  const hasAbout = !!(pageData.value && pageData.value.availablePages && pageData.value.availablePages.about);
  if (hasAbout) {
    links.push({ label: 'About', url: '/about' });
  }

  // Always include Privacy and Terms (these are often present)
  links.push({ label: 'Privacy', url: '/privacy-policy' });
  links.push({ label: 'Terms', url: '/terms-of-service' });
  // ENG b-policies: the shop's Returns policy and Terms of sale, once published
  // (the classic footer of Flat, Glass and Night Watch; the new footers use legalLinks).
  links.push(...classicPolicyLinks(pageData.value?.availablePages?.policies));

  return links;
})


// A themed hero (T1 slot) can render a fragment or an async component, so its
// $el may be a text or comment node: walk to the first element, or skip it.
function heroElement(hero) {
  let n = hero?.$el ?? hero
  while (n && n.nodeType !== 1) n = n.nextSibling
  return n && typeof n.getBoundingClientRect === "function" ? n : null
}

function updatePinnedHero() {
  if (!herosReady.value) return;
  if (!heroRefs.value?.length) return

  let activeHero = null

  for (const hero of heroRefs.value) {
    const el = heroElement(hero)
    if (!el) continue
    const rect = el.getBoundingClientRect()
    if (rect.top <= 0) activeHero = hero
  }

  if (activeHero) {
    const heroRefIndex = heroRefs.value.indexOf(activeHero)
    // find corresponding hero in pageData.sections
    let sectionIndex = -1
    let heroCounter = -1
    for (let i = 0; i < shown.value.length; i++) {
      if (shown.value[i].section.type === 'hero') heroCounter++
      if (heroCounter === heroRefIndex) {
        sectionIndex = i
        break
      }
    }
    pinnedHeroIndex.value = sectionIndex
  } else {
    pinnedHeroIndex.value = 0
  }
}

onMounted(() => {
  window.addEventListener("scroll", updatePinnedHero, { passive: true });
  herosReady.value = true;
});
onUnmounted(() => {
  window.removeEventListener("scroll", updatePinnedHero);
});

// Keep heroRefs in sync after DOM changes
watch(
  () => shown.value.length,
  () => nextTick(() => {
    // Vue 3: $refs.heroRefs is an array of component instances or elements
    // This line ensures heroRefs.value is always up to date
    heroRefs.value = (Array.isArray(heroRefs.value) ? heroRefs.value : []).filter(Boolean);
    updatePinnedHero();
  })
);

vueOnMounted(async () => {
  if (import.meta.env.VITE_IS_PREVIEW_SERVER === 'true'){
    previewMode.value = true;
  }
  window.testError = () => { throw new Error("Client test error"); };

  if (pageData.value?.sections?.length > 0) updateMetaTags();
  else {
    const segments = route.path.split("/");
    const pageType = route.path === '/' ? 'home' : segments[1];
    const pageName = segments.length > 2 ? segments[segments.length - 1] : '';
    isFetching.value = true;
    window.scrollTo(0, 0);
    await fetchPageData(pageType, pageName);
  }

  nextTick(() => {
    if (navBarRef.value) {
      navHeight.value = navBarRef.value.barHeight();
    }
    loaded.value = true;
    isFetching.value = false;
    // Ensure heroRefs is up to date after mount
    heroRefs.value = (Array.isArray(heroRefs.value) ? heroRefs.value : []).filter(Boolean);
    updatePinnedHero();
  });
});

const backgroundColourAccent = computed(() => (
  {backgroundColor: pageData?.value?.brandColors?.accent})
)

const productLink = computed(() => {
  switch (pageData?.value?.productPageName) {
    case "Services":
      return `/services`;
    case "Products":
      return `/products`;
    case "What we do":
      return `/what-we-do`;
    default:
      return `/products`;
  }
});

// Canonical --brand-* custom properties, derived once here and inherited by
// every descendant instead of each component re-deriving its own local alias.
const brandCssVars = useBrandCssVars(computed(() => pageData.value?.brandColors ?? {}));

// Theme-level design tokens (typography, etc.) — set once here, same pattern
// as brand colors, so components can opt in via var(--theme-*) without any
// per-component plumbing.
const themeTokens = useThemeTokens();

// ✅ Compute styles dynamically
const pageStyles = computed(() => ({
  // backgroundColor: pageData.value?.brandColors?.background || "#ffffff",
  color: pageData.value?.brandColors?.text || "#000000",
  transition: "background-color 0.5s ease-in-out, color 0.5s ease-in-out",
  fontFamily: "var(--theme-font-family, inherit)",
  letterSpacing: "var(--theme-tracking, normal)",
  ...brandCssVars.value,
  ...themeTokens.value,
}));

const navClass = computed(() =>
    pageData.value?.brandColors?.navBackground ? "bg-nova-darkest text-white" : "bg-nova-lightest text-black"
);

onUnmounted(() => {
  pageData.value.sections = [];
});

// The title and share tags after a page change in the browser: the same ones entry-server.js renders (ogTags.js), so
// search engines that run the page see the page's own title, description and photo, never placeholders.
const updateMetaTags = () => {
  if (!pageData.value || typeof window === "undefined") return;
  const og = ogData({pageData: pageData.value, origin: window.location.origin, path: route.fullPath});
  useHead({
    title: og.title,
    meta: [{name: "description", content: og.description}, ...ogMeta(og)],
  });
  document.title = og.title;
};


const showCredits = () => {
  credits.value.openBox()
}


</script>
<style scoped>

.studio-empty {
  width: calc(100% - 32px);
  max-width: 72rem;
  margin: 16px;
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  border: 2px dashed #e0a24a;
  border-radius: 10px;
  background: #fffaf1;
  color: #8a4b00;
  font: 600 15px/1.3 "Public Sans", system-ui, sans-serif;
}

/* Page Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease-in-out, transform 0.5s ease-in-out;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(20px); /* Slight upwards movement */
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-20px); /* Slight fade-out movement */
}

.list-enter-active,
.list-leave-active {
  transition: all 2.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
  transition: all 2.5s ease;

}

</style>

<style>
@keyframes knightRider {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(200%);
  }
}

.animate-knight-rider {
  width: 33%;
  height: 100%;
  animation: knightRider 1.5s linear infinite alternate;
  transform: translateX(-100%);
  border-radius: 5px;
}

</style>

